import {
  AssessmentEntry,
  AssessmentEntryScore,
  AssessmentMethodScore,
  isAssessmentEntryScore,
} from '@schooly/api';
import { CellParams } from '@schooly/components/data-grid';
import { ScoreCell } from '@schooly/style';
import React from 'react';

import { getScorePercentageValue } from '../../../../../components/uikit-components/AssessmentEntryMethods/utils';
import { CellColorProps, getCellTextColor } from '../colouredCells';
import { ScoreUpdateParams } from '../handlers/scoreHandlers';
import { useMarkbookCellContext } from '../MarkbookCellContext';
import { AssessmentCellState, MarkbookForGroupRow, MarkbookForGroupsRow } from '../types';

type ScoreCellRendererProps = {
  method: AssessmentMethodScore;
  entries: AssessmentEntry[];
  canEdit: boolean;
  groupId: string;
  assessmentId: string;
  subjectId: string;
  studentRelationId: string;
  onScoreUpdate: (params: ScoreUpdateParams) => Promise<void>;
  getAssessmentEntryColor?: (cellProps: CellColorProps) => string | null;
  scoreControllerProps?: Record<string, unknown>;
  categoryId?: string;
  isDisabled?: boolean;
} & CellParams<MarkbookForGroupRow | MarkbookForGroupsRow, AssessmentCellState>;

export const ScoreCellRenderer = ({
  method,
  canEdit,
  isDisabled,
  onScoreUpdate,
  assessmentId,
  groupId,
  subjectId,
  scoreControllerProps,
  studentRelationId,
  entries,
  row,
  field,
  cellState,
  categoryId,
}: ScoreCellRendererProps) => {
  const { showPercentage, getAssessmentEntryColor } = useMarkbookCellContext();

  const entry = entries.find((e) => e.method.id === method.id);

  const relationId = studentRelationId;
  const status = cellState?.status ?? null;
  const isEditing = scoreControllerProps?.focused;
  const color =
    entry && !isEditing
      ? getAssessmentEntryColor?.({
          studentRelationId,
          assessmentId,
          groupId,
          subjectId,
          entry: entry as AssessmentEntryScore,
          method,
        })
      : null;

  const getTextColor = () => {
    if (isDisabled) return ['common.grey6', 'common.grey6'] as [string, string];
    if (color) return getCellTextColor(color);
    return undefined;
  };

  return (
    <ScoreCell
      value={entry && isAssessmentEntryScore(entry) ? entry.score : null}
      scoreOutOf={method.score_out_of}
      status={status}
      backgroundColor={color}
      canEdit={canEdit}
      textColor={getTextColor()}
      getDisplayValue={
        showPercentage && !isEditing
          ? (value, scoreOutOf) => `${getScorePercentageValue(value, scoreOutOf)}%`
          : undefined
      }
      onUpdate={(score) =>
        onScoreUpdate({
          cellId: `${row.id}_${field}`,
          rowId: row.id,
          field,
          studentId: relationId,
          methodId: method.id ?? '',
          assessmentId,
          score,
          groupId,
          categoryId,
        })
      }
      {...scoreControllerProps}
    />
  );
};
